import React, { useState } from 'react';
import { X, Loader2, CheckCircle, Send } from 'lucide-react';
import { quotesAPI } from '../../services/api';

const COUNTRIES = [
  'United States', 'United Kingdom', 'Germany', 'France', 'Spain', 'Italy',
  'Netherlands', 'Belgium', 'Austria', 'Portugal', 'Ireland', 'Sweden',
  'Denmark', 'Finland', 'Poland', 'Czech Republic', 'Greece', 'Hungary',
  'Romania', 'Bulgaria', 'Croatia', 'Slovakia', 'Slovenia', 'Lithuania',
  'Latvia', 'Estonia', 'Cyprus', 'Malta', 'Luxembourg', 'Canada',
  'Australia', 'New Zealand', 'Japan', 'Singapore', 'Switzerland', 'Norway',
  'Other'
];

const QuoteRequestModal = ({ isOpen, onClose, artwork }) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    country: '',
    message: '',
    is_framed: false,
  });

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await quotesAPI.create({
        artwork_id: artwork.id,
        artwork_title: artwork.title,
        artwork_type: artwork.artwork_type || 'painting',
        ...formData,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      customer_name: '',
      customer_email: '',
      country: '',
      message: '',
      is_framed: false,
    });
    setSuccess(false);
    setError('');
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-lg font-medium">Request Shipping Quote</h2>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-full"
              data-testid="close-quote-modal-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {success ? (
              <div className="text-center py-8 space-y-4">
                <CheckCircle className="w-16 h-16 mx-auto text-green-500" />
                <h3 className="text-xl font-medium">Request Submitted!</h3>
                <p className="text-gray-600">
                  Thank you for your interest in "{artwork.title}".
                </p>
                <p className="text-sm text-gray-500">
                  We'll get back to you within 1-2 business days with a shipping quote.
                </p>
                <button
                  onClick={handleClose}
                  className="bg-black text-white px-6 py-2 rounded-md hover:bg-gray-800"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">{artwork.title}</span>
                  </p>
                  <p className="text-sm text-gray-500">
                    {artwork.artwork_type === 'painting' ? 'Original Painting' : 'Print'} - Shipping quote required
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Your Name *</label>
                    <input
                      type="text"
                      value={formData.customer_name}
                      onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                      required
                      className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
                      data-testid="quote-input-name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Email Address *</label>
                    <input
                      type="email"
                      value={formData.customer_email}
                      onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                      required
                      className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
                      data-testid="quote-input-email"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Shipping Country *</label>
                    <select
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      required
                      className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
                      data-testid="quote-select-country"
                    >
                      <option value="">Select country</option>
                      {COUNTRIES.map(country => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>
                  </div>

                  {artwork.artwork_type === 'print' && (
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="is_framed"
                        checked={formData.is_framed}
                        onChange={(e) => setFormData({ ...formData, is_framed: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <label htmlFor="is_framed" className="text-sm">
                        I would like this print framed
                      </label>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium mb-1">Message (optional)</label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={3}
                      className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black resize-none"
                      placeholder="Any specific requirements or questions?"
                      data-testid="quote-input-message"
                    />
                  </div>

                  {error && (
                    <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-black text-white py-3 rounded-md hover:bg-gray-800 transition-colors disabled:bg-gray-400 flex items-center justify-center gap-2"
                    data-testid="submit-quote-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Request Quote
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default QuoteRequestModal;
